import React, { PropsWithChildren, useMemo } from 'react';
import { LDProvider, ProviderConfig } from 'launchdarkly-react-client-sdk';
import { LDEvaluationDetail } from 'launchdarkly-js-client-sdk';

import { config } from 'common/config';
import { useSelector } from 'react-redux';
import { selectUser, selectUserCompany } from '../../redux/user/selectors';

/**
 * Local development override: flag values supplied as a JSON object are handed to the
 * LaunchDarkly client as its bootstrap set, so the app serves them without a usable
 * clientSideID. Mirrors DevProvider on the API side (api/src/feature-flags/feature-provider.ts).
 * Unset in every deployed environment, where the value stays 'localStorage'.
 */
function parseLocalFlagOverrides(): Record<string, unknown> | null {
  const raw = process.env.NEXT_PUBLIC_FEATURE_FLAG_VALUES;

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    console.warn('NEXT_PUBLIC_FEATURE_FLAG_VALUES is not valid JSON — falling back to LaunchDarkly');
    return null;
  }
}

// Inlined at build time, so this is a module-level constant rather than a hook.
const localFlagOverrides = parseLocalFlagOverrides();

export function FeatureFlagProvider({ children }: PropsWithChildren) {
  const user = useSelector(selectUser);
  const userCompany = useSelector(selectUserCompany);

  const companyCode = userCompany?.code;
  const userId = user?.id;
  const userEmail = user?.emailAddress;

  const ldUserDetails = useMemo(() => {
    if (!companyCode || !userId || !userEmail) {
      return undefined;
    }

    const isE2eUser = userEmail.includes('e2e');

    if (isE2eUser) {
      return  {
        kind: 'multi',
        company: {
          key: 'BCE2E',
        },
      };
    }

    return  {
      kind: 'multi',
      company: {
        key: companyCode,
      },
      user: {
        key: String(userId),
        email: userEmail.toLowerCase(),
        _meta: {
          privateAttributes: ['email'],
        },
      },
    };
  }, [companyCode, userId, userEmail]);

  const providerOptions: ProviderConfig = useMemo(() => {
    return {
      clientSideID: config?.launchDarklyClientId || '',
      deferInitialization: true,
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      options: {
        bootstrap: localFlagOverrides ?? 'localStorage',
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDEvaluationDetail) => {
              if (window.DD_RUM) {
                window.DD_RUM.addFeatureFlagEvaluation(key, detail.value);
              }
            },
          },
        ],
      },
      context: ldUserDetails,
    } as ProviderConfig
  }, [ldUserDetails]);

  return (
    <LDProvider{...providerOptions}>
        {children}
    </LDProvider>
  );
}
